import type { TokenCredential } from "@azure/identity";
import { LocalCliError } from "./errors.js";
import { HOSTED_AGENT_REGIONS, isHostedRegionSupported } from "./regions.js";
import { imageTagExists } from "./acr.js";
import { callerCanAssignRoles, grantAcrPull, principalHasAcrPull, FOUNDRY_USER_ROLE_ID } from "./rbac.js";
import type { FoundryProject } from "./foundry-project.js";

export interface PreconditionResult { warnings: string[] }

/** Parse "examplecr0000.azurecr.io/m8t-coding-agent:tag" → { host, acrName, repo, tag }. */
export function parseImageRef(image: string): { host: string; acrName: string; repo: string; tag: string } {
  const [hostAndPath, tag] = image.split(":");
  const slash = hostAndPath.indexOf("/");
  const host = hostAndPath.slice(0, slash);
  const repo = hostAndPath.slice(slash + 1);
  const acrName = host.replace(/\.azurecr\.io$/i, "");
  return { host, acrName, repo, tag: tag || "" };
}

/** True for an Azure Container Registry host (…azurecr.io). Public registries (ghcr.io, docker.io) → false. */
export function isAcrHost(host: string): boolean {
  return /\.azurecr\.io$/i.test(host);
}

/** ARM resource id of the ACR (scope for the AcrPull check/grant). */
function acrScope(subscriptionId: string, projectAccountScope: string, acrName: string): string {
  const rg = (/\/resourceGroups\/([^/]+)\//.exec(projectAccountScope))?.[1] ?? "";
  return `/subscriptions/${subscriptionId}/resourceGroups/${rg}/providers/Microsoft.ContainerRegistry/registries/${acrName}`;
}

export async function checkPreconditions(args: {
  credential: TokenCredential;
  subscriptionId: string;
  project: FoundryProject;
  image: string;
  callerObjectId: string;
}): Promise<PreconditionResult> {
  const warnings: string[] = [];

  // 1. Region — hard block.
  if (!isHostedRegionSupported(args.project.region)) {
    throw new LocalCliError({
      code: "REGION_NOT_SUPPORTED",
      message: `Project '${args.project.projectName}' is in '${args.project.region}', which does not support hosted agents.`,
      hint: `Hosted agents need one of: ${HOSTED_AGENT_REGIONS.join(", ")}. Deploy against a project in a supported region.`,
    });
  }

  // 2 + 3. ACR-only preconditions. A public image (e.g. ghcr.io) is pulled
  // anonymously: there is no ACR to check the tag in and no AcrPull to grant.
  const { host, acrName, repo, tag } = parseImageRef(args.image);
  if (isAcrHost(host)) {
    // 2. Image tag in ACR — hard block if definitively absent; warn if unknown.
    const tagCheck = await imageTagExists(acrName, repo, tag);
    if (tagCheck === "absent") {
      throw new LocalCliError({
        code: "IMAGE_TAG_NOT_FOUND",
        message: `Image '${repo}:${tag}' was not found in ACR '${acrName}'.`,
        hint: `Build + push it first: cd agents/coding-agent && docker buildx build --platform linux/amd64 -t ${acrName}.azurecr.io/${repo}:${tag} --push . (see guides/workers.md).`,
      });
    }
    if (tagCheck === "unknown") {
      warnings.push(`could not verify image '${repo}:${tag}' in ACR '${acrName}' (az acr check failed) — proceeding; a bad image surfaces as a failed version.`);
    }

    // 3. Project MI AcrPull — grant if missing (operator is verified able to assign in step 4 next; grant is idempotent).
    if (args.project.projectPrincipalId) {
      const scope = acrScope(args.subscriptionId, args.project.accountScope, acrName);
      const hasPull = await principalHasAcrPull({
        credential: args.credential,
        acrScope: scope,
        principalId: args.project.projectPrincipalId,
      });
      if (!hasPull) {
        await grantAcrPull({
          credential: args.credential,
          subscriptionId: args.subscriptionId,
          acrScope: scope,
          principalId: args.project.projectPrincipalId,
        });
        warnings.push(`granted AcrPull to the project managed identity on '${acrName}' (was missing).`);
      }
    } else {
      warnings.push("could not resolve the project managed identity to verify AcrPull — ensure it can pull from the ACR.");
    }
  } else {
    // Defensive fallback only: the hosted-agent deploy commands stage public images
    // into the customer ACR first (see image-staging.ts → stagePublicImageIfNeeded),
    // so an ACR ref is the norm by the time we get here. Post-2026-06-25 Foundry
    // projects reject anonymous pulls, so a public ref reaching this branch unstaged
    // would fail provisioning with ImageError.
    warnings.push(`public image host '${host}' — skipping the ACR tag check and AcrPull grant (no staging applied; provisioning will fail on a post-2026-06-25 project).`);
  }

  // 4. Caller can assign roles — hard block (we must grant Foundry User after create).
  const canAssign = await callerCanAssignRoles({
    credential: args.credential,
    scope: args.project.accountScope,
    callerObjectId: args.callerObjectId,
  });
  if (!canAssign) {
    throw new LocalCliError({
      code: "CALLER_CANNOT_ASSIGN_ROLES",
      message: "You lack permission to create role assignments on the Foundry account, which is required to grant the hosted agent its runtime role.",
      hint: `Ask an Owner / User Access Administrator to grant you one of those roles on ${args.project.accountScope}, or to run (after the agent is created): az role assignment create --assignee-object-id <agentOID> --assignee-principal-type ServicePrincipal --role ${FOUNDRY_USER_ROLE_ID} --scope ${args.project.accountScope}`,
    });
  }

  return { warnings };
}
